import { Flex, Table, TableColumnsType } from 'antd';
import { useMemo, useState } from 'react';
import { UserAddModalWrapper } from '../user-add-modal';

import { Title } from 'components/mvp-typography';
import { CrxSearch } from '../../../../../components/search-component/search-component';
import { useUserTableColumns } from './hooks/use-userTableColumns';
import {
  BaseTableUserRecord,
  TableRow,
  UserListProps
} from './user-table-types';

/**
 * A table with a searchable filter to list and modify users.
 * Design expectations - users are pending or confirmed, users are added with invites.
 *
 * @param props
 * @returns
 */
export default function UserTable<BaseRecord extends BaseTableUserRecord>(
  props: UserListProps<BaseRecord>
) {
  // The table filter
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  /**
   * Apply search filters if they exist
   *
   * WARNING: In the worst case this doubles the memory allocated for table data.
   */
  const filteredData = useApplySearch(searchQuery, props.data);

  function onSearch(val: string) {
    if (val === '') {
      setSearchQuery(null);
      return;
    }
    setSearchQuery(val);
  }

  async function onAddUser(_userData: unknown) {
    await props.onAddUser(_userData as any, props.clinicId);
    props.refetchData();
  }

  /**
   * The columns will only change when extraColumns changes
   */
  const columns: TableColumnsType<TableRow<BaseRecord>> = useUserTableColumns(
    props,
    props.refetchData
  );

  const searchPlaceholder = [
    'Search',
    props.typeUnit ? props.typeUnit + 's' : null
  ]
    .filter(_ => !!_)
    .join(' ');

  return (
    <div>
      <Flex
        justify={'space-between'}
        align="center"
        style={{
          marginBottom: 16,
          alignContent: 'center',
          alignItems: 'center'
        }}
      >
        <Title level={2}>{props.typeUnit}s</Title>
        <div>
          <CrxSearch onSearch={onSearch} placeholder={searchPlaceholder} />
        </div>
      </Flex>

      <Table
        style={{
          width: '100%'
        }}
        pagination={{ defaultPageSize: 5 }}
        loading={props.isLoading}
        dataSource={filteredData}
        columns={columns}
      />

      <UserAddModalWrapper
        containerStyle={{
          /**
           * Align the button with the table pagination component.
           * The pagination component is nested in the AntD table component and is not accessible
           */
          marginTop: '-3.2rem',
          /**
           * Increase the z-index to allow the button to be clicked if the table renders something above it
           */
          zIndex: 10
        }}
        onAddUser={onAddUser}
        type={props.typeUnit.toLowerCase()}
        questions={props.userAddQuestions}
      />
    </div>
  );
}

/**
 * Apply search filters if they exist
 *
 * WARNING: In the worst case this doubles the memory allocated for table data.
 */
function useApplySearch<T extends BaseTableUserRecord>(
  searchQuery: string | null,
  data: TableRow<T>[]
) {
  return useMemo(() => {
    // Case 1: There is no filter, return the original data
    if (searchQuery === null) return data;

    // Case 2: There is a filter
    return data.filter(record =>
      // Create a searchable index
      JSON.stringify({
        name: [record.firstName, record.lastName].join(' '),
        firstName: record.firstName,
        lastName: record.lastName,
        email: record.email,
        id: record.raw.id
      })
        .toLowerCase()
        // Include results that match the search query
        .includes(searchQuery.toLowerCase())
    );
  }, [data, searchQuery]);
}
